'use client';

import * as React from 'react';
import { useCallback } from 'react';
import ActiveLink from '@/components/ui/ActiveLink';
import { IHeaderLink } from '@/components/ui/Header';

export interface ISmartLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  link: IHeaderLink;
  children: React.ReactNode;
  className?: string;
  exact?: boolean;
  useActiveLink?: boolean;
}

const SmartLink: React.FunctionComponent<ISmartLinkProps> = ({
  link,
  children,
  className,
  exact = false,
  useActiveLink = false,
  ...props
}) => {
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (link.href.startsWith('#')) {
        const target = link.href.substring(1);
        const elem = document.getElementById(target);
        if (elem) {
          e.preventDefault();
          elem.scrollIntoView({
            block: 'start',
            behavior: 'smooth',
          });
        }
        return;
      }

      if (link.showModal) {
        e.preventDefault();

        const hideModalKey = `hide-external-link-modal-${link.href}`;
        const shouldHideModal = localStorage.getItem(hideModalKey) === 'true';

        if (shouldHideModal) {
          if (link.openInNewTab) {
            window.open(link.href, '_blank', 'noopener,noreferrer');
          } else {
            window.location.href = link.href;
          }
        }
      }
    },
    [link],
  );

  const commonProps = {
    href: link.href,
    target: link.openInNewTab ? '_blank' : link.target,
    rel: 'noopener noreferrer',
    onClick: handleClick,
    className,
    ...props,
  };

  if (useActiveLink) {
    return (
      <ActiveLink
        exact={exact}
        {...commonProps}
      >
        {children}
      </ActiveLink>
    );
  }

  return <a {...commonProps}>{children}</a>;
};

SmartLink.displayName = 'SmartLink';

export default SmartLink;
